import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { AsYouType, CountryCode } from 'libphonenumber-js/max';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  PhoneCountry,
  formatNationalMask,
  getFieldMaxLength,
  getPhoneCountry
} from '../../phone/phone-countries.data';
import {
  E164_MAX_DIGITS,
  PhoneKind,
  buildStoredPhone,
  detectCountryFromInput,
  internationalDigits,
  onlyDigits,
  parseStoredPhone,
  phoneValidationError,
  toStoredPhone
} from '../../phone/phone-format';

const INTERNATIONAL_MAX_LENGTH = 24;

function normalizeSearch(value: string): string {
  return (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

@Component({
  selector: 'app-intl-phone-input',
  templateUrl: './intl-phone-input.component.html',
  styleUrls: ['./intl-phone-input.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IntlPhoneInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => IntlPhoneInputComponent), multi: true }
  ]
})
export class IntlPhoneInputComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() kind: PhoneKind = 'mobile';
  @Input() inputId = 'intlPhoneInput';

  @Output() phoneBlur = new EventEmitter<void>();

  @ViewChild('phoneInput', { static: true }) phoneInput: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('toggleButton', { static: true }) toggleButton: ElementRef<HTMLButtonElement>;

  country: CountryCode = DEFAULT_PHONE_COUNTRY;
  displayValue = '';
  disabled = false;

  isOpen = false;
  search = '';
  filteredCountries: PhoneCountry[] = PHONE_COUNTRIES;
  activeIndex = 0;

  /** Valor recebido por writeValue; enquanto o usuário não editar, o validador não bloqueia (dados legados). */
  private writtenValue = '';
  private edited = false;
  /** Último valor armazenado recebido/emitido: só notifica o form quando muda (não marca dirty à toa). */
  private lastStored = '';
  /** Dígitos nacionais do que está sendo digitado como "+DDI..." (o campo mostra o "+" até o foco sair). */
  private plusNational = '';

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };
  private onValidatorChange: () => void = () => { };

  constructor(private host: ElementRef, private cdr: ChangeDetectorRef) { }

  get selectedCountry(): PhoneCountry {
    return getPhoneCountry(this.country);
  }

  get maxLength(): number {
    return this.displayValue.charAt(0) === '+' ? INTERNATIONAL_MAX_LENGTH : getFieldMaxLength(this.country);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kind'] && !changes['kind'].firstChange) {
      this.onValidatorChange();
    }
  }

  // ---- ControlValueAccessor / Validator ----

  writeValue(value: string): void {
    this.writtenValue = value || '';
    this.lastStored = value || '';
    this.edited = false;
    this.plusNational = '';
    const parsed = parseStoredPhone(value);
    this.country = parsed.country;
    this.setDisplay(this.formatDigits(parsed.national));
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.close();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || (!this.edited && value === this.writtenValue)) {
      return null;
    }
    const { country, national } = parseStoredPhone(value);
    const error = phoneValidationError(country, national, this.kind);
    if (error === 'invalid') {
      return { phonePattern: true };
    }
    return error === 'notMobile' ? { phoneNotMobile: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // ---- Input ----

  onInput(): void {
    const input = this.phoneInput.nativeElement;
    this.applyRaw(input.value, input.selectionStart, true);
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    if (this.disabled || this.phoneInput.nativeElement.readOnly) {
      return;
    }
    const text = event.clipboardData ? event.clipboardData.getData('text') : '';
    this.applyRaw(text, null, false);
  }

  /** Foco saiu do componente inteiro (input, botão de país ou busca). */
  onFocusOut(event: FocusEvent): void {
    const related = event.relatedTarget as Node;
    if (related && this.host.nativeElement.contains(related)) {
      return;
    }
    if (this.displayValue.charAt(0) === '+') {
      if (this.plusNational) {
        // País e número já resolvidos: passa para o formato nacional.
        this.setDisplay(this.formatDigits(this.plusNational));
      } else if (!onlyDigits(this.displayValue) || detectCountryFromInput(this.displayValue)) {
        // Só "+" ou "+DDI" reconhecido sem número nacional: limpa o texto solto.
        // DDI desconhecido é mantido para o validador acusar (phonePattern).
        this.setDisplay('');
        this.emit('');
      }
      this.plusNational = '';
    } else if (this.country === DEFAULT_PHONE_COUNTRY && this.displayValue.charAt(0) === '0') {
      // Tronco/operadora digitado: agora o número está completo e pode ser resolvido
      this.applyRaw(this.displayValue, null, false);
    }
    this.onTouched();
    this.phoneBlur.emit();
  }

  /** Escape com o painel aberto fecha só o painel (não o modal que contém o campo). */
  onEscape(event: Event): void {
    if (this.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleButton.nativeElement.focus();
      this.close();
    }
  }

  /**
   * Texto começando com "+" (ou "00" + DDI) reavalia o país a cada alteração. Ao digitar, o campo continua mostrando
   * o "+DDI..." até o foco sair; ao colar, já vai para o formato nacional.
   */
  private applyRaw(raw: string, caret: number | null, typing: boolean): void {
    const text = (raw || '').trim();

    const intl = internationalDigits(text);
    if (intl !== null) {
      // "00" + DDI equivale a "+": descarta o "00" para tratar igual
      const dialPrefixLength = text.charAt(0) === '+' ? 0 : 2;
      const typed = intl.slice(0, E164_MAX_DIGITS);
      const detected = detectCountryFromInput('+' + typed);
      if (detected) {
        this.setCountry(detected.country);
      }
      const national = detected ? detected.national.slice(0, this.selectedCountry.digitCap) : '';
      // O armazenado é sempre o que aparece (nacional já limitado). DDI desconhecido fica como digitado, para o
      // validador acusar (mesmo comportamento do clube-web), em vez de virar vazio.
      const stored = detected ? (national ? buildStoredPhone(this.country, national) : '') : (typed ? '+' + typed : '');

      if (detected && !typing) {
        this.plusNational = '';
        this.setDisplay(this.formatDigits(national));
        this.emit(stored);
        return;
      }

      this.plusNational = national;
      this.setDisplay(new AsYouType().input('+' + typed));
      if (caret !== null) {
        const digitsBeforeCaret = Math.max(0, Math.min(onlyDigits(raw.slice(0, caret)).length - dialPrefixLength, typed.length));
        if (digitsBeforeCaret < typed.length) {
          this.setCaretAfterDigits(digitsBeforeCaret);
        }
      }
      this.emit(stored);
      return;
    }

    this.plusNational = '';
    let digits = onlyDigits(text);
    if (this.country === DEFAULT_PHONE_COUNTRY && digits.charAt(0) === '0' && typing) {
      // Tronco/operadora ("0 21 85...") ou "00" sendo digitado: enquanto parcial é ambíguo (0+21 parece DDD), então
      // mostra os dígitos crus e só resolve quando o foco sair ou ao colar. O armazenado sai sempre do texto inteiro.
      digits = digits.slice(0, E164_MAX_DIGITS);
      this.setDisplay(digits);
      this.emit(toStoredPhone(DEFAULT_PHONE_COUNTRY, digits));
      return;
    }
    if (this.country === DEFAULT_PHONE_COUNTRY && (digits.length > this.selectedCountry.digitCap || digits.charAt(0) === '0')) {
      // Remove 55, tronco "0" e operadora ANTES do limite de dígitos
      digits = onlyDigits(toStoredPhone(DEFAULT_PHONE_COUNTRY, digits));
    }
    digits = digits.slice(0, this.selectedCountry.digitCap);

    const digitsBeforeCaret = caret === null ? digits.length : Math.min(onlyDigits(raw.slice(0, caret)).length, digits.length);
    this.setDisplay(this.formatDigits(digits));
    if (caret !== null && digitsBeforeCaret < digits.length) {
      this.setCaretAfterDigits(digitsBeforeCaret);
    }
    this.emit(this.country === DEFAULT_PHONE_COUNTRY ? digits : toStoredPhone(this.country, digits));
  }

  private formatDigits(digits: string): string {
    // Remove separadores sobrando no fim (ex.: ")" ou " "), senão o backspace não consegue apagar a máscara.
    return digits ? formatNationalMask(this.country, digits).replace(/[^0-9]+$/, '') : '';
  }

  private setDisplay(value: string): void {
    this.displayValue = value;
    if (this.phoneInput) {
      this.phoneInput.nativeElement.value = value;
    }
  }

  private setCaretAfterDigits(digitCount: number): void {
    const input = this.phoneInput.nativeElement;
    let position = 0;
    let seen = 0;
    while (position < input.value.length && seen < digitCount) {
      if (/\d/.test(input.value.charAt(position))) {
        seen++;
      }
      position++;
    }
    input.setSelectionRange(position, position);
  }

  private setCountry(country: CountryCode): void {
    if (country !== this.country) {
      this.country = country;
    }
  }

  private emit(stored: string): void {
    if (stored === this.lastStored) {
      return;
    }
    this.lastStored = stored;
    this.edited = true;
    this.onChange(stored);
  }

  // ---- Seletor de país ----

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen ? this.close() : this.open();
  }

  openFromKeyboard(event: Event): void {
    event.preventDefault();
    if (!this.disabled && !this.isOpen) {
      this.open();
    }
  }

  open(): void {
    this.isOpen = true;
    this.search = '';
    this.filteredCountries = PHONE_COUNTRIES;
    this.activeIndex = Math.max(this.filteredCountries.findIndex(c => c.iso2 === this.country), 0);
    this.cdr.detectChanges();
    if (this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
    this.scrollToActive();
  }

  close(): void {
    this.isOpen = false;
  }

  onSearch(value: string): void {
    this.search = value;
    const term = normalizeSearch(value).replace(/^\+/, '');
    this.filteredCountries = !term
      ? PHONE_COUNTRIES
      : PHONE_COUNTRIES.filter(c =>
        normalizeSearch(c.name).indexOf(term) >= 0 ||
        c.dialCode.indexOf(term) === 0 ||
        c.iso2.toLowerCase().indexOf(term) >= 0
      );
    this.activeIndex = 0;
    this.scrollToActive();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.filteredCountries[this.activeIndex]) {
          this.select(this.filteredCountries[this.activeIndex]);
        }
        break;
      case 'Tab':
        // Foco primeiro no botão (o painel e a busca somem ao fechar), depois o Tab segue dali
        this.toggleButton.nativeElement.focus();
        this.close();
        break;
    }
  }

  select(country: PhoneCountry): void {
    const changed = country.iso2 !== this.country;
    this.phoneInput.nativeElement.focus(); // antes de fechar, para o focusout não sair do componente
    this.close();
    if (changed) {
      this.setCountry(country.iso2);
      // Reaplica a máscara do novo país sobre os dígitos já digitados.
      const source = this.displayValue.charAt(0) === '+' ? this.plusNational : onlyDigits(this.displayValue);
      this.plusNational = '';
      const digits = source.slice(0, country.digitCap);
      this.setDisplay(this.formatDigits(digits));
      this.emit(toStoredPhone(this.country, digits));
    }
  }

  private moveActive(step: number): void {
    const total = this.filteredCountries.length;
    if (!total) {
      return;
    }
    this.activeIndex = (this.activeIndex + step + total) % total;
    this.scrollToActive();
  }

  private scrollToActive(): void {
    this.cdr.detectChanges();
    const active = this.host.nativeElement.querySelector('.intl-phone__item.active');
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest' });
    }
  }
}
